import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { FilterCell, FilterSummary, SortableHeader } from '../../components/TableHeads'
import { TableScroll } from '../../components/TableScroll'
import { formatCost, formatDateTime, formatNumber } from '../../lib/format'
import type { TableColumn } from '../../lib/tableView'
import { useTableView } from '../../lib/useTableView'
import type { ReadingAttempt, ReadOutcome, ReadTrigger } from './api'

const TRIGGERS: readonly ReadTrigger[] = ['Arrival', 'Escalation', 'Requested']
const OUTCOMES: readonly ReadOutcome[] = [
  'Read',
  'Refused',
  'CutOff',
  'Unavailable',
  'Unreadable',
  'NotARateConfirmation',
  'Interrupted',
]

/** A reading's tokens, in and out; null when the reading reported none — unknown, never zero. */
function tokensOf(attempt: ReadingAttempt): number | null {
  return attempt.inputTokens === null || attempt.outputTokens === null ? null : attempt.inputTokens + attempt.outputTokens
}

/**
 * Every column sorts and filters (TRKC-110). A figure filters by "at least", in the unit the
 * column shows — seconds for the time — and a reading whose tokens or cost are unknown sorts
 * last and never passes a figure.
 */
function columnsFor(t: TFunction): TableColumn<ReadingAttempt>[] {
  return [
    {
      key: 'sequence',
      sortValue: (attempt) => attempt.sequence,
      filter: { kind: 'atLeast', value: (attempt) => attempt.sequence, keepUnknown: false },
    },
    {
      key: 'trigger',
      sortValue: (attempt) => t(`rateConfirmations.triggers.${attempt.trigger}`),
      filter: { kind: 'choice', values: TRIGGERS, matches: (attempt, chosen) => attempt.trigger === chosen },
    },
    {
      key: 'model',
      sortValue: (attempt) => attempt.servedModel ?? attempt.model,
      filter: { kind: 'text', text: (attempt) => `${attempt.model} ${attempt.servedModel ?? ''}` },
    },
    {
      key: 'outcome',
      sortValue: (attempt) => t(`rateConfirmations.outcomesShort.${attempt.outcome}`),
      filter: { kind: 'choice', values: OUTCOMES, matches: (attempt, chosen) => attempt.outcome === chosen },
    },
    {
      key: 'tokens',
      sortValue: tokensOf,
      filter: { kind: 'atLeast', value: tokensOf, keepUnknown: false },
    },
    {
      key: 'cost',
      sortValue: (attempt) => attempt.estimatedCostUsd,
      filter: { kind: 'atLeast', value: (attempt) => attempt.estimatedCostUsd, keepUnknown: false },
    },
    {
      key: 'duration',
      sortValue: (attempt) => attempt.durationMilliseconds,
      filter: { kind: 'atLeast', value: (attempt) => attempt.durationMilliseconds / 1000, keepUnknown: false },
    },
  ]
}

const HEADINGS: Record<string, string> = {
  sequence: 'rateConfirmations.attempts.sequence',
  trigger: 'rateConfirmations.attempts.trigger',
  model: 'rateConfirmations.attempts.model',
  outcome: 'rateConfirmations.attempts.outcome',
  tokens: 'rateConfirmations.attempts.tokens',
  cost: 'rateConfirmations.attempts.cost',
  duration: 'rateConfirmations.attempts.duration',
}

/**
 * Every reading of the file, in order: why it ran, which model it asked and which answered,
 * how it ended, and what it cost — a reading costs money every time it runs (D5), so the
 * record shows each one. The view lives in the page and starts over for another record
 * (TRKC-110).
 */
export function AttemptsTable({ attempts }: { attempts: readonly ReadingAttempt[] }) {
  const { t, i18n } = useTranslation()
  const language = i18n.language
  const columns = useMemo(() => columnsFor(t), [t])
  const view = useTableView(attempts, columns)
  const options = (key: 'triggers' | 'outcomesShort', values: readonly string[]) =>
    values.map((value) => ({ value, label: t(`rateConfirmations.${key}.${value}`) }))

  return (
    <section className="rc-section">
      <h2 className="rc-section__title">{t('rateConfirmations.attempts.title')}</h2>
      <TableScroll label={t('rateConfirmations.attempts.title')}>
        <table className="table">
          <caption className="visually-hidden">{t('rateConfirmations.attempts.title')}</caption>
          <thead>
            <tr>
              {columns.map((column) => (
                <SortableHeader key={column.key} view={view} column={column.key}>
                  {t(HEADINGS[column.key] ?? column.key)}
                </SortableHeader>
              ))}
            </tr>
            <tr className="table__filters">
              {columns.map((column) => (
                <FilterCell
                  key={column.key}
                  view={view}
                  column={column}
                  label={t(HEADINGS[column.key] ?? column.key)}
                  options={
                    column.key === 'trigger'
                      ? options('triggers', TRIGGERS)
                      : column.key === 'outcome'
                        ? options('outcomesShort', OUTCOMES)
                        : undefined
                  }
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {view.rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="placeholder">
                  {t('table.noneMatch')}
                </td>
              </tr>
            )}
            {view.rows.map((attempt) => (
              <tr key={attempt.sequence}>
                <td className="table__numeric">
                  {attempt.sequence}
                  <br />
                  <span className="muted">{formatDateTime(attempt.startedAt, language)}</span>
                </td>
                <td>{t(`rateConfirmations.triggers.${attempt.trigger}`)}</td>
                <td className="rc-attempts__model">
                  {attempt.model}
                  {attempt.servedModel !== null && attempt.servedModel !== attempt.model && (
                    <>
                      <br />
                      <span className="muted">{t('rateConfirmations.attempts.answeredBy', { model: attempt.servedModel })}</span>
                    </>
                  )}
                  {attempt.effort !== null && (
                    <>
                      <br />
                      <span className="muted">{t('rateConfirmations.attempts.effort', { effort: attempt.effort })}</span>
                    </>
                  )}
                </td>
                <td>
                  {t(`rateConfirmations.outcomesShort.${attempt.outcome}`)}
                  {attempt.current && (
                    <>
                      <br />
                      <span className="muted">{t('rateConfirmations.attempts.shown')}</span>
                    </>
                  )}
                </td>
                <td className="table__numeric">
                  {attempt.inputTokens === null || attempt.outputTokens === null
                    ? '—'
                    : t('rateConfirmations.attempts.tokensInOut', {
                        input: formatNumber(attempt.inputTokens, language),
                        output: formatNumber(attempt.outputTokens, language),
                      })}
                </td>
                <td className="table__numeric">
                  {attempt.estimatedCostUsd === null ? (
                    <span className="muted">{t('rateConfirmations.attempts.costUnknown')}</span>
                  ) : (
                    formatCost(attempt.estimatedCostUsd, language)
                  )}
                </td>
                <td className="table__numeric">
                  {t('rateConfirmations.attempts.seconds', {
                    seconds: formatNumber(Math.round(attempt.durationMilliseconds / 100) / 10, language),
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
      <FilterSummary view={view} />
    </section>
  )
}
